/**
 * InboxContent — web port of `apps/WunderGuide/components/inbox/InboxOverlay.tsx`.
 *
 * Two dropdown filters (category + status) → date-grouped notification list.
 * Calls GET /notifications/inbox; falls back to demo entries when the
 * endpoint isn't available yet. Filter UI mirrors RN's modal Dropdowns
 * (NOT pills) — see `InboxOverlay.tsx` `Dropdown` component.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import inboxApi from '../../api/inboxApi';
import type { Notification, NotificationCategory } from '../../api/inboxApi';
import { setupWebPush } from '../../lib/webPush';
import { useLanguage } from '../../hooks/useLanguage';
import CommonSpinner from '../../common/Spinner';
import './InboxContent.css';

type Category = 'all' | NotificationCategory;
type Status = 'all' | 'unread' | 'read';

// 1:1 with RN CATEGORY_KEYS (`InboxOverlay.tsx` line 82). The web
// NotificationCategory union additionally has `system` and `trip` but RN
// does not surface those as filter options; rows of those categories
// still appear under "All Types".
const CATEGORY_OPTIONS: { key: Category; label: string }[] = [
  { key: 'all', label: 'All Types' },
  { key: 'booking', label: 'Bookings' },
  { key: 'message', label: 'Messages' },
  { key: 'itinerary', label: 'Itinerary' },
  { key: 'reminder', label: 'Reminders' },
  { key: 'promotion', label: 'Promos' },
];

const STATUS_OPTIONS: { key: Status; label: string }[] = [
  { key: 'all', label: 'Any Status' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

const DEMO_NOTIFICATIONS: Notification[] = [
  {
    id: 'demo-1',
    user_id: 'demo',
    title: 'Welcome to WunderGuide',
    body: 'Tap the mic to start planning your trip with Leilani.',
    category: 'system',
    status: 'unread',
    priority: 'normal',
    created_at: new Date(Date.now() - 5 * 60_000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 60_000).toISOString(),
  } as Notification,
  {
    id: 'demo-2',
    user_id: 'demo',
    title: 'Saturday luau · 5:00 PM',
    body: 'Toa Luau at Waimea Valley — 2 seats held.',
    category: 'booking',
    status: 'unread',
    priority: 'normal',
    created_at: new Date(Date.now() - 90 * 60_000).toISOString(),
    updated_at: new Date(Date.now() - 90 * 60_000).toISOString(),
  } as Notification,
  {
    id: 'demo-3',
    user_id: 'demo',
    title: 'Diamond Head sunrise hike',
    body: 'Tomorrow at 6:30 AM — sunrise is 6:42 AM.',
    category: 'trip',
    status: 'read',
    priority: 'normal',
    created_at: new Date(Date.now() - 12 * 3600_000).toISOString(),
    updated_at: new Date(Date.now() - 12 * 3600_000).toISOString(),
  } as Notification,
  {
    id: 'demo-4',
    user_id: 'demo',
    title: '20% off shave ice today',
    body: 'Matsumoto in Haleiwa — show this in-app.',
    category: 'promotion',
    status: 'read',
    priority: 'normal',
    created_at: new Date(Date.now() - 26 * 3600_000).toISOString(),
    updated_at: new Date(Date.now() - 26 * 3600_000).toISOString(),
  } as Notification,
];

const InboxContent: React.FC = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<Category>('all');
  const [status, setStatus] = useState<Status>('all');
  const [pushPermission, setPushPermission] = useState<
    NotificationPermission | 'unsupported'
  >(
    typeof Notification === 'undefined'
      ? 'unsupported'
      : Notification.permission,
  );

  const refresh = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    inboxApi
      .getNotifications({ limit: 50 })
      .then(res => {
        if (cancelled) return;
        const fetched = res.notifications ?? [];
        // RN screenshot-mode parity: when the backend has no notifications
        // (or the endpoint isn't deployed yet), show a small set of demo
        // entries so the inbox isn't an empty void on first run.
        setItems(fetched.length > 0 ? fetched : DEMO_NOTIFICATIONS);
        setError(null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          // Endpoint not available — fall back to demo entries.
          setItems(DEMO_NOTIFICATIONS);
        } else {
          setError(
            t('COULD_NOT_LOAD_NOTIFICATIONS') ||
              'Could not load notifications.',
          );
          setItems(DEMO_NOTIFICATIONS);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return refresh();
  }, [refresh]);

  const handleMarkAllRead = useCallback(() => {
    inboxApi
      .markAllAsRead()
      .then(() => refresh())
      .catch(() => {});
  }, [refresh]);

  const handleRowPress = useCallback((n: Notification) => {
    if (n.status === 'unread') {
      inboxApi.markAsRead(n.id).catch(() => {});
      setItems(prev =>
        prev.map(it =>
          it.id === n.id ? { ...it, status: 'read' as const } : it,
        ),
      );
    }
    const url = n.deep_link || n.actions?.[0]?.url;
    if (url) {
      if (/^https?:/.test(url)) {
        window.open(url, '_blank', 'noopener');
      } else {
        window.location.assign(url);
      }
    }
  }, []);

  const filtered = useMemo(() => {
    return items
      .filter(n => (category === 'all' ? true : n.category === category))
      .filter(n => (status === 'all' ? true : n.status === status))
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [items, category, status]);

  const sections = useMemo(() => groupByDate(filtered), [filtered]);

  const unreadCount = items.filter(n => n.status === 'unread').length;

  return (
    <div className="wg-inbox">
      <div className="wg-inbox__filters">
        <Dropdown
          options={CATEGORY_OPTIONS}
          value={category}
          onChange={v => setCategory(v as Category)}
        />
        <Dropdown
          options={STATUS_OPTIONS}
          value={status}
          onChange={v => setStatus(v as Status)}
        />
      </div>

      {unreadCount > 0 && (
        <button
          type="button"
          className="wg-inbox__mark-all"
          onClick={handleMarkAllRead}
        >
          <CheckDoneIcon />
          Mark all as read
        </button>
      )}

      {pushPermission === 'default' && (
        <button
          type="button"
          className="wg-inbox__push-cta"
          onClick={() => {
            setupWebPush().then(r => setPushPermission(r.permission));
          }}
        >
          <BellIcon />
          Enable push notifications
        </button>
      )}

      {loading && (
        <div className="wg-inbox__empty">
          <CommonSpinner size="medium" />
        </div>
      )}

      {!loading && error && (
        <div className="wg-inbox__empty">
          <AlertIcon />
          <div className="wg-inbox__empty-text">{error}</div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="wg-inbox__empty">
          <BellOffIcon />
          <div className="wg-inbox__empty-text">
            {items.length === 0
              ? t('NO_NOTIFICATIONS') || 'No notifications yet.'
              : t('NO_MATCHING_NOTIFICATIONS') || 'No matching notifications.'}
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="wg-inbox__list">
          {sections.map(sec => (
            <section key={sec.key} className="wg-inbox__section">
              <div className="wg-inbox__section-title">{sec.label}</div>
              {sec.items.map(n => (
                <NotificationRow key={n.id} n={n} onPress={handleRowPress} />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default InboxContent;

/* ── Sub-components ────────────────────────────────────────────────────── */

interface DropdownProps<T extends string> {
  options: { key: T; label: string }[];
  value: T;
  onChange: (k: T) => void;
}

/**
 * Dropdown — mirrors RN's `InboxOverlay.tsx` Dropdown component (a
 * Pressable that opens a modal selector). Web uses a styled native
 * `<select>` for accessibility; visual chrome is a glass pill with a
 * trailing chevron.
 */
function Dropdown<T extends string>({
  options,
  value,
  onChange,
}: DropdownProps<T>) {
  const selected = options.find(o => o.key === value)?.label ?? value;
  return (
    <label className="wg-inbox__dropdown">
      <span className="wg-inbox__dropdown-value">{selected}</span>
      <ChevronDownIcon />
      <select
        className="wg-inbox__dropdown-select"
        value={value}
        onChange={e => onChange(e.target.value as T)}
      >
        {options.map(o => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const NotificationRow: React.FC<{
  n: Notification;
  onPress: (n: Notification) => void;
}> = ({ n, onPress }) => {
  return (
    <button
      type="button"
      className={`wg-inbox__row${n.status === 'unread' ? ' is-unread' : ''}`}
      onClick={() => onPress(n)}
    >
      <span className="wg-inbox__row-icon">
        <CategoryIcon category={n.category} />
      </span>
      <span className="wg-inbox__row-text">
        <span className="wg-inbox__row-title">{n.title}</span>
        {n.body && <span className="wg-inbox__row-body">{n.body}</span>}
      </span>
      <span className="wg-inbox__row-time">{formatRelative(n.created_at)}</span>
    </button>
  );
};

function CategoryIcon({ category }: { category: Notification['category'] }) {
  switch (category) {
    case 'booking':
      return <CalendarCheckIcon />;
    case 'message':
      return <MessageIcon />;
    case 'itinerary':
    case 'trip':
      return <RouteIcon />;
    case 'reminder':
    case 'system':
      return <BellIcon />;
    case 'promotion':
      return <SparkleIcon />;
  }
}

interface Section {
  key: string;
  label: string;
  items: Notification[];
}

function groupByDate(items: Notification[]): Section[] {
  const groups: Record<string, Notification[]> = {};
  const today = startOfDay(new Date());
  const yesterday = startOfDay(new Date(today.getTime() - 86_400_000));
  const week = startOfDay(new Date(today.getTime() - 7 * 86_400_000));
  for (const n of items) {
    const t = startOfDay(new Date(n.created_at)).getTime();
    let key: string;
    if (t === today.getTime()) key = 'today';
    else if (t === yesterday.getTime()) key = 'yesterday';
    else if (t > week.getTime()) key = 'week';
    else key = 'older';
    (groups[key] ??= []).push(n);
  }
  const order: Array<[string, string]> = [
    ['today', 'Today'],
    ['yesterday', 'Yesterday'],
    ['week', 'This week'],
    ['older', 'Older'],
  ];
  return order
    .filter(([k]) => groups[k]?.length)
    .map(([k, label]) => ({ key: k, label, items: groups[k] }));
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatRelative(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

/* ── Icons ─────────────────────────────────────────────────────────────── */

function ChevronDownIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="13" />
      <circle cx="12" cy="16.5" r="0.6" fill="currentColor" />
    </svg>
  );
}
function BellOffIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9.5 4.5a6 6 0 0 1 8.5 5.5c0 4 2 5 2 7H8" />
      <path d="M10 19a2 2 0 0 0 4 0" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </svg>
  );
}
function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 4 2 5 2 7H4c0-2 2-3 2-7z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  );
}
function CalendarCheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="17" rx="2.5" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
      <polyline points="9 14 11.5 16.5 16 12" />
    </svg>
  );
}
function MessageIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l1.6-5A8.5 8.5 0 1 1 21 11.5z" />
    </svg>
  );
}
function RouteIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M6 17V9a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v0" />
    </svg>
  );
}
function SparkleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6z" />
    </svg>
  );
}
function CheckDoneIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3 12 8 17 14 7" />
      <polyline points="11 17 16 21 22 11" />
    </svg>
  );
}
